import type { Allocated } from "../types/Allocated"
import type { Pointer } from "../types/Pointer"
import type { WasmExportConfig } from "../types/WasmExportConfig"
import type { WasmInstance } from "../types/WasmInstance"
import type { WasmBinary } from "../utils/ResolveWasm"
import { UrlLoad } from "@pdfking/compatibility"
import { Ceil, Max, Min } from "@pdfking/math"
import {
  Constant,
  IsFunction,
  Items,
  Keys,
  RecordAssign,
  UInt8ToString,
  Void,
} from "@pdfking/tfunc"
import { Minimal } from "../config/Minimal"
import { DefaultMinimap } from "../maps/DefaultMinimap"
import { Stub } from "../types/WasmExportConfig"
import { ResolveBinary } from "../utils/ResolveWasm"

const MergeConfiguration = (
  overrides: Partial<WasmExportConfig>
): WasmExportConfig => ({
  ...Minimal,
  ...overrides,
  stubs: { ...Minimal.stubs, ...overrides.stubs },
  invokes: { ...Minimal.invokes, ...overrides.invokes },
  wasi_stubs: { ...Minimal.wasi_stubs, ...overrides.wasi_stubs },
})

// ── ConvertJsFunctionToWasm helpers ─────────────────────────────────

const WASM_TYPE_NAMES: Readonly<Record<string, string>> = {
  d: "f64",
  f: "f32",
  i: "i32",
  j: "i64",
  p: "i32",
}

const WASM_TYPE_CODES: Readonly<Record<string, number>> = {
  d: 124,
  f: 125,
  i: 127,
  j: 126,
  p: 127,
}

const SigToWasmTypes = (sig: string) => ({
  parameters: [...sig.slice(1)].map(c => WASM_TYPE_NAMES[c]),
  results: sig[0] === "v" ? [] : [WASM_TYPE_NAMES[sig[0]]],
})

const Uleb128Encode = (n: number, target: number[]) => {
  if (n < 128) target.push(n)
  else target.push((n % 128) | 128, n >> 7)
}

const GenerateFuncType = (sig: string, target: number[]) => {
  const sigParam = sig.slice(1)
  target.push(96)
  Uleb128Encode(sigParam.length, target)
  for (const c of sigParam) target.push(WASM_TYPE_CODES[c])
  if (sig[0] === "v") target.push(0)
  else target.push(1, WASM_TYPE_CODES[sig[0]])
}

const ConvertJsFunctionToWasm = (
  func: (...args: number[]) => number | bigint,
  sig: string
): WebAssembly.ExportValue => {
  const WasmFunction = (WebAssembly as unknown as Record<string, unknown>)
    .Function as new (
    type: unknown,
    func: unknown
  ) => WebAssembly.ExportValue
  if (IsFunction(WasmFunction)) {
    return new WasmFunction(SigToWasmTypes(sig), func)
  }
  const typeSectionBody: number[] = [1]
  GenerateFuncType(sig, typeSectionBody)
  const bytes: number[] = [0, 97, 115, 109, 1, 0, 0, 0, 1]
  Uleb128Encode(typeSectionBody.length, bytes)
  bytes.push(...typeSectionBody)
  bytes.push(2, 7, 1, 1, 101, 1, 102, 0, 0, 7, 5, 1, 1, 102, 0, 0)
  const module = new WebAssembly.Module(new Uint8Array(bytes))
  const instance = new WebAssembly.Instance(module, { e: { f: func } })
  return instance.exports.f
}

// ── CreateEmscriptenImports ─────────────────────────────────────────

const Fail = (msg: string): never => {
  throw new Error(msg)
}

const DATE = Constant(1704067200000)
const MAX_HEAP_32 = 4294967296 // 4 GB
const MAX_HEAP_64 = 17179869184 // 16 GB
const Align = (size: number, alignment: number) =>
  Ceil(size / alignment) * alignment

/**
 * Create an invoke handler that uses dynCall exports for indirect calls.
 * With EMULATE_FUNCTION_POINTER_CASTS, all table functions use (i64, i64, ...)
 * ABI and cannot be called directly from JS. The dynCall_* WASM exports handle
 * the type conversion internally.
 */
const CreateInvoke =
  (
    exports: Record<string, unknown>,
    config: WasmExportConfig,
    dynCallName: string
  ) =>
  (
    index: number,
    ...args: (number | bigint)[]
  ): number | bigint | undefined => {
    const stackGet = exports[config.stack_get_current!] as () => number
    const sp = stackGet()
    try {
      const dynCall = exports[dynCallName] as
        | ((...a: (number | bigint)[]) => number | bigint)
        | undefined
      if (dynCall) return dynCall(index, ...args)
      const table = exports[config.indirect_func_table!] as WebAssembly.Table
      const fn = table.get(index) as
        | ((...a: (number | bigint)[]) => number | bigint)
        | null
      if (fn) return fn(...args)
    } catch (e: unknown) {
      const stackRestore = exports[config.stack_restore!] as (
        sp: number
      ) => void
      stackRestore(sp)
      if (typeof e !== "number" && typeof e !== "bigint") throw e
      const setThrew = exports[config.set_threw!] as (
        a: number,
        b: number
      ) => void
      setThrew(1, 0)
    }
    return undefined
  }

const CreateEmscriptenImports = (
  config: WasmExportConfig
): {
  readonly exports: Record<string, unknown>
  readonly imports: WebAssembly.Imports
} => {
  const exports = {} as Record<string, unknown>
  const moduleImports: WebAssembly.ModuleImports = {}

  if (config.invokes) {
    for (const [name, dynCallName] of Items(config.invokes)) {
      moduleImports[name] = CreateInvoke(exports, config, dynCallName)
    }
  }

  const mem = () => exports[config.memory] as WebAssembly.Memory
  const maxHeap =
    config.maximum_memory ?? (config.memory64 ? MAX_HEAP_64 : MAX_HEAP_32)

  const stubs: Record<Stub, WebAssembly.ModuleImports[string]> = {
    [Stub.noop]: Void,
    [Stub.zero]: Constant(0),
    [Stub.enoent]: Constant(-44),
    [Stub.ebadf]: Constant(8),
    [Stub.ebadf_neg]: Constant(-8),
    [Stub.abort]: () => Fail("abort"),
    [Stub.cxa_throw]: (ptr: number | bigint) => {
      throw ptr
    },
    [Stub.throw_longjmp]: () => {
      throw Infinity
    },
    [Stub.exit]: (status: number | bigint) => {
      throw { status: Number(status) }
    },
    [Stub.date_now]: DATE,
    [Stub.environ_sizes_get]: (
      countPtr: number | bigint,
      sizePtr: number | bigint
    ) => {
      const heap = new DataView(mem().buffer)
      heap.setInt32(Number(countPtr), 0, true)
      heap.setInt32(Number(sizePtr), 0, true)
      return 0
    },
    [Stub.assert_fail]: () => Fail("assertion failed"),
    [Stub.getcwd]: (buf: number | bigint) => {
      const b = Number(buf)
      const heap = new DataView(mem().buffer)
      heap.setUint8(b, 47)
      heap.setUint8(b + 1, 0)
      return buf
    },
    [Stub.resize_heap]: (requestedSize: number | bigint) => {
      const m = mem()
      const oldSize = m.buffer.byteLength
      const requested = config.memory64
        ? Number(requestedSize)
        : Number(requestedSize) >>> 0
      if (requested > maxHeap) return 0
      for (let cutDown = 1; cutDown <= 4; cutDown *= 2) {
        let overGrownHeapSize = oldSize * (1 + 0.2 / cutDown)
        overGrownHeapSize = Min(overGrownHeapSize, requested + 100663296)
        const newSize = Min(
          maxHeap,
          Align(Max(requested, overGrownHeapSize), 65536)
        )
        const pagesToGrow = ((newSize - oldSize + 65535) / 65536) | 0
        try {
          m.grow(pagesToGrow)
          return 1
        } catch {
          // try again with less overallocation
        }
      }
      return 0
    },
  }

  if (config.stubs) {
    for (const [name, kind] of Items(config.stubs)) {
      moduleImports[name] = stubs[kind]
    }
  }

  // Built-in log_str: Rust code can call rust_log("...") to print to console
  moduleImports[DefaultMinimap.log_str as string] = (
    ptr: number | bigint,
    len: number | bigint
  ) => {
    const bytes = new Uint8Array(mem().buffer, Number(ptr), Number(len))
    console.log(UInt8ToString(bytes))
  }

  const imports: WebAssembly.Imports = {
    [config.import_module]: moduleImports,
  }

  if (config.wasi_stubs) {
    const wasiImports: WebAssembly.ModuleImports = {}
    for (const [name, kind] of Items(config.wasi_stubs)) {
      wasiImports[name] = stubs[kind]
    }
    if (config.wasi_module) imports[config.wasi_module] = wasiImports
  }

  if (config.build_imports) {
    const custom = config.build_imports(exports)
    for (const [name, fn] of Items(custom.env)) {
      moduleImports[name] = fn
    }
    if (config.wasi_module) {
      const wasiImports =
        (imports[config.wasi_module] as WebAssembly.ModuleImports) ?? {}
      for (const [name, fn] of Items(custom.wasi)) {
        wasiImports[name] = fn
      }
      imports[config.wasi_module] = wasiImports
    }
  }

  return { exports, imports }
}

// ── CreateWasmLoader ────────────────────────────────────────────────

/**
 * Compile a WASM module from a URL and return a factory that creates WasmInstance objects.
 * The module is compiled once; each call to the factory creates a fresh instance.
 */
export const CreateWasmLoader = async (
  binary: WasmBinary,
  overrides: Partial<WasmExportConfig> = {}
): Promise<() => Promise<WasmInstance>> => {
  const { url, memory64 } = ResolveBinary(binary)
  const config = MergeConfiguration({ ...overrides, memory64 })
  const response = await UrlLoad(url, "application/wasm")
  const module = await WebAssembly.compileStreaming(response)

  return async () => {
    const { imports, exports } = CreateEmscriptenImports(config)

    const init = () =>
      WebAssembly.instantiate(module, imports).then(instance => {
        RecordAssign(exports, instance.exports)
        if (config.wasm_call_ctors) {
          const ctors = exports[config.wasm_call_ctors] as () => void
          ctors()
        }
        if (config.init) config.init(exports)
      })

    await init()

    const addCallback = (
      fn: (...args: number[]) => number | bigint,
      sig: string
    ): Allocated => {
      const tableName = config.indirect_func_table!
      const wrapped = ConvertJsFunctionToWasm(fn, sig)
      const table = exports[tableName] as WebAssembly.Table
      const index = table.length
      table.grow(1)
      table.set(index, wrapped)
      const pointer = BigInt(index) as Pointer
      return {
        free: () => (exports[tableName] as WebAssembly.Table).set(index, null),
        pointer,
      }
    }

    const dispose = async () => {
      for (const key of Keys(exports)) {
        delete exports[key]
      }
    }

    return {
      addCallback,
      dispose,
      exports,
      init,
      memory64: !!config.memory64,
    }
  }
}
